/**
 * Consents tab — list signed consents + new consent capture flow.
 * Supports both typed-name and drawn-signature methods.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ShieldOff, ShieldCheck, FileSignature, X } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";

const CONSENT_TYPES = [
  { v: "services_and_care", l: "Consent for Services & Care",
    body: "I authorize the agency and its caregivers to provide services and care as outlined in the care plan, including assistance with activities of daily living, medication reminders, and other services agreed upon." },
  { v: "release_of_info", l: "Release of Client Information",
    body: "I authorize the release of medical and personal information necessary for the coordination of my care to: physicians, pharmacies, hospitals, family members listed on my contact sheet, and other authorized care providers." },
  { v: "photograph", l: "Photograph / Video Consent",
    body: "I consent to the taking of photographs and/or video recordings for documentation of my condition (e.g., wound progression, skin assessments) for use in my medical record only." },
  { v: "treatment", l: "Treatment Consent", body: "" },
  { v: "financial", l: "Financial Responsibility", body: "" },
  { v: "other", l: "Other", body: "" },
];

const RELATIONSHIPS = [
  { v: "client", l: "Client (self)" }, { v: "spouse", l: "Spouse" },
  { v: "child", l: "Child" }, { v: "parent", l: "Parent" }, { v: "sibling", l: "Sibling" },
  { v: "poa", l: "Power of attorney" }, { v: "guardian", l: "Legal guardian" },
  { v: "other", l: "Other" },
];

const GRANT_PRESETS = ["family", "physician", "pharmacy", "hospital", "social_services", "billing"];

function ConsentForm({ clientId, onCreated, onCancel }) {
  const [type, setType] = useState("services_and_care");
  const typeMeta = CONSENT_TYPES.find((t) => t.v === type);
  const [label, setLabel] = useState("");
  const [bodyText, setBodyText] = useState(typeMeta?.body || "");
  const [signerName, setSignerName] = useState("");
  const [relationship, setRelationship] = useState("client");
  const [witnessName, setWitnessName] = useState("");
  const [validFrom, setValidFrom] = useState(new Date().toISOString().slice(0, 10));
  const [validUntil, setValidUntil] = useState("");
  const [grantedTo, setGrantedTo] = useState([]);
  const [notes, setNotes] = useState("");
  // Signature
  const [method, setMethod] = useState("typed");
  const [typedName, setTypedName] = useState("");
  const [drawnDataUrl, setDrawnDataUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const onTypeChange = (newType) => {
    setType(newType);
    const meta = CONSENT_TYPES.find((t) => t.v === newType);
    setBodyText(meta?.body || "");
  };

  const toggleGrant = (g) => setGrantedTo((arr) =>
    arr.includes(g) ? arr.filter((x) => x !== g) : [...arr, g]
  );

  const submit = async () => {
    if (!signerName.trim()) { toast.error("Signer name required"); return; }
    if (method === "typed" && !typedName.trim()) { toast.error("Type your full name to sign"); return; }
    if (method === "drawn" && !drawnDataUrl) { toast.error("Please draw your signature"); return; }
    setSaving(true);
    try {
      const payload = {
        type, label, body_text: bodyText,
        signer_name: signerName, signer_relationship: relationship,
        granted_to: grantedTo, valid_from: validFrom, valid_until: validUntil,
        witness_name: witnessName, notes,
        signature: {
          method,
          typed_name: method === "typed" ? typedName : "",
          drawn_data_url: method === "drawn" ? drawnDataUrl : "",
        },
      };
      await api.post(`/clients/${clientId}/consents`, payload);
      toast.success("Consent captured");
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="card-surface p-4 space-y-3" data-testid="consent-form">
      <div className="flex items-center justify-between">
        <h3 className="label-eyebrow">New consent</h3>
        <button type="button" onClick={onCancel}
          className="text-clinical-subtext hover:text-clinical-text"
          aria-label="Cancel">
          <X size={16} />
        </button>
      </div>
      <div>
        <label htmlFor="consent-type" className="text-xs text-clinical-subtext">Consent type</label>
        <select id="consent-type" value={type} onChange={(e) => onTypeChange(e.target.value)}
          className="input-clinical mt-1" data-testid="consent-type">
          {CONSENT_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
        </select>
      </div>
      {type === "other" && (
        <div>
          <label htmlFor="consent-label" className="text-xs text-clinical-subtext">Label</label>
          <input id="consent-label" type="text" value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Transportation consent" className="input-clinical mt-1"
            data-testid="consent-label" />
        </div>
      )}
      <div>
        <label htmlFor="consent-body" className="text-xs text-clinical-subtext">Consent statement</label>
        <textarea id="consent-body" rows={3} value={bodyText} onChange={(e) => setBodyText(e.target.value)}
          className="input-clinical resize-y mt-1" data-testid="consent-body" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="consent-signer" className="text-xs text-clinical-subtext">Signer name</label>
          <input id="consent-signer" type="text" value={signerName} onChange={(e) => setSignerName(e.target.value)}
            className="input-clinical mt-1" data-testid="consent-signer-name" />
        </div>
        <div>
          <label htmlFor="consent-rel" className="text-xs text-clinical-subtext">Relationship</label>
          <select id="consent-rel" value={relationship} onChange={(e) => setRelationship(e.target.value)}
            className="input-clinical mt-1" data-testid="consent-relationship">
            {RELATIONSHIPS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="consent-witness" className="text-xs text-clinical-subtext">Witness name (optional)</label>
          <input id="consent-witness" type="text" value={witnessName} onChange={(e) => setWitnessName(e.target.value)}
            className="input-clinical mt-1" data-testid="consent-witness" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label htmlFor="consent-from" className="text-xs text-clinical-subtext">Valid from</label>
            <input id="consent-from" type="date" value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)} className="input-clinical mt-1"
              data-testid="consent-valid-from" />
          </div>
          <div>
            <label htmlFor="consent-until" className="text-xs text-clinical-subtext">Until (optional)</label>
            <input id="consent-until" type="date" value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)} className="input-clinical mt-1"
              data-testid="consent-valid-until" />
          </div>
        </div>
      </div>

      {type === "release_of_info" && (
        <div>
          <div className="label-eyebrow mb-1">Information may be released to</div>
          <div className="flex flex-wrap gap-1.5">
            {GRANT_PRESETS.map((g) => (
              <button key={g} type="button" onClick={() => toggleGrant(g)}
                className={`rounded-md px-2 py-1 text-xs font-medium border transition ${
                  grantedTo.includes(g)
                    ? "border-clinical-accent bg-clinical-accent/10 text-clinical-accent"
                    : "border-clinical-border bg-white text-clinical-subtext"
                }`}
                data-testid={`consent-grant-${g}`}
              >{g.replace(/_/g, " ")}</button>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="label-eyebrow mb-2">Signature</div>
        <div className="flex gap-2 mb-3">
          {["typed", "drawn"].map((m) => (
            <button key={m} type="button" onClick={() => setMethod(m)}
              className={`flex-1 rounded-lg border py-1.5 text-xs font-medium transition ${
                method === m
                  ? "border-clinical-accent bg-clinical-accent/10 text-clinical-accent"
                  : "border-clinical-border bg-white text-clinical-subtext"
              }`}
              data-testid={`consent-method-${m}`}
            >{m === "typed" ? "Type name" : "Draw signature"}</button>
          ))}
        </div>
        {method === "typed" ? (
          <div>
            <input type="text" value={typedName} onChange={(e) => setTypedName(e.target.value)}
              placeholder="Type your full legal name"
              className="input-clinical font-serif text-lg italic"
              data-testid="consent-typed-name" />
            <p className="mt-1 text-[11px] text-clinical-subtext">
              By typing your name, you electronically sign this document. This signature is legally
              equivalent to a handwritten signature under the E-SIGN Act.
            </p>
          </div>
        ) : (
          <SignaturePad onChange={setDrawnDataUrl} />
        )}
      </div>

      <div>
        <label htmlFor="consent-notes" className="text-xs text-clinical-subtext">Notes (internal)</label>
        <textarea id="consent-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
          className="input-clinical resize-y mt-1" data-testid="consent-notes" />
      </div>

      <div className="flex gap-2 pt-1">
        <button type="button" onClick={onCancel} className="flex-1 btn-secondary"
          data-testid="consent-cancel">Cancel</button>
        <button type="button" onClick={submit} disabled={saving} className="flex-1 btn-primary"
          data-testid="consent-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />}
          Capture consent
        </button>
      </div>
    </div>
  );
}

function ConsentRow({ consent, onRevoke, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`card-surface p-3 ${consent.revoked ? "opacity-60" : ""}`}
      data-testid={`consent-row-${consent.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start justify-between gap-3 text-left">
        <div>
          <div className="flex items-center gap-2">
            <FileSignature size={14} className="text-clinical-subtext" />
            <span className="font-medium text-clinical-text">{consent.label}</span>
            {consent.revoked && (
              <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700">
                Revoked
              </span>
            )}
          </div>
          <div className="text-xs text-clinical-subtext mt-0.5">
            Signed by {consent.signer_name} ({consent.signer_relationship}) on{" "}
            {new Date(consent.signature.signed_at).toLocaleDateString()}
          </div>
        </div>
      </button>
      {open && (
        <div className="mt-3 border-t border-clinical-border/40 pt-3 space-y-2 text-sm">
          {consent.body_text && (
            <div>
              <div className="label-eyebrow">Consent statement</div>
              <p className="mt-1 whitespace-pre-line text-clinical-text">{consent.body_text}</p>
            </div>
          )}
          {consent.signature.method === "drawn" && consent.signature.drawn_data_url && (
            <div>
              <div className="label-eyebrow">Signature</div>
              <img src={consent.signature.drawn_data_url} alt="Signature"
                className="mt-1 max-h-24 border border-clinical-border rounded-lg bg-white" />
            </div>
          )}
          {consent.signature.method === "typed" && (
            <div className="font-serif text-lg italic text-clinical-text">
              {consent.signature.typed_name}
            </div>
          )}
          {consent.witness_name && (
            <div className="text-xs text-clinical-subtext">Witnessed by {consent.witness_name}</div>
          )}
          {consent.granted_to?.length > 0 && (
            <div className="text-xs">
              <span className="text-clinical-subtext">Granted to: </span>
              {consent.granted_to.join(", ")}
            </div>
          )}
          <div className="text-[11px] text-clinical-subtext">
            Captured by {consent.created_by_name} · IP {consent.signature.ip_address || "—"}
          </div>
          {!consent.revoked && (
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => onRevoke?.(consent.id)}
                className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800"
                data-testid={`consent-revoke-${consent.id}`}>
                <ShieldOff size={12} /> Revoke
              </button>
              <button type="button" onClick={() => onDelete?.(consent.id)}
                className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700"
                data-testid={`consent-delete-${consent.id}`}>
                <Trash2 size={12} /> Delete
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ClientConsentsTab({ clientId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/consents`);
      setItems(data);
    } catch { /* swallow */ }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const onCreated = () => { setAdding(false); load(); };
  const onRevoke = async (id) => {
    try {
      await api.post(`/clients/${clientId}/consents/${id}/revoke`);
      toast.success("Consent revoked");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Permanently delete this consent record?")) return;
    try {
      await api.delete(`/clients/${clientId}/consents/${id}`);
      toast.success("Consent deleted");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-clinical-subtext">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const active = items.filter((c) => !c.revoked).length;

  return (
    <div className="space-y-3" data-testid="client-consents-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <ShieldCheck size={14} className="text-green-600" />
          <span className="font-medium">{active} active consent{active === 1 ? "" : "s"}</span>
          {items.length > active && (
            <span className="text-clinical-subtext">· {items.length - active} revoked</span>
          )}
        </div>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} className="btn-primary !py-1.5 !text-xs"
            data-testid="consent-add-btn">
            <Plus className="h-3.5 w-3.5" /> Capture consent
          </button>
        )}
      </div>

      {adding && <ConsentForm clientId={clientId} onCreated={onCreated} onCancel={() => setAdding(false)} />}

      {items.length === 0 && !adding ? (
        <div className="card-surface p-8 text-center text-clinical-subtext text-sm">
          No consents captured yet.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <ConsentRow key={c.id} consent={c} onRevoke={onRevoke} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
}
